// Ports of the 0-1 / 0-3 yr branches of growthscreener::calculate_advice_*
// (AGPL-3.0; NCJ JGZ richtlijnen ondergewicht 2019 / overgewicht 2012 /
// lengtegroei 2019). Uses the Dutch references (NL5 wfa, NL4=NL1997 hdc,
// NL5/NL4 hfa) so SDS lines up with the chart bands.
//
// The JGZ printout itself uses NL3 for the SDS column on the printed
// summary — we deliberately diverge so the on-screen SDS matches the
// bands the parent is looking at.
//
// All msgcodes match the upstream JGZ catalogue so the wording can be
// swapped for clinical-grade text without code changes.
import { interpolateLMS, normalCdf, valueToSDS, type Sex, type LMSRow } from './lms.ts';
import { getReferenceTable, pickWeightReference } from './tables.ts';

export type AdviceMeasurement = {
  /** Unix seconds. */
  occurredAt: number;
  /** Value in the unit the algorithm expects (kg for weight, cm for length/HC). */
  value: number;
};

export type SdsTrailEntry = { ageYr: number; sds: number | null };

export type AdviceResult<Code extends number = number> = {
  msgcode: Code;
  message: string;
  /** SDS of the latest measurement. null when undefined (missing inputs, age
   *  outside table, out-of-scope branch). */
  sdsNow: number | null;
  /** Percentile (0..100). null when sdsNow is null. */
  percentile: number | null;
  /** Per-measurement SDS trail, chronological. May contain nulls. */
  sdsTrail: SdsTrailEntry[];
};

/** Mirror growthscreener::date2age: round(days/365.25, 4). Returns years. */
export function date2age(birthSec: number, atSec: number): number {
  const days = (atSec - birthSec) / 86_400;
  return Math.round((days / 365.25) * 1e4) / 1e4;
}

function buildResult<C extends number>(
  code: C,
  message: string,
  sdsNow: number | null,
  sdsTrail: SdsTrailEntry[],
): AdviceResult<C> {
  return {
    msgcode: code,
    message,
    sdsNow,
    percentile: sdsNow == null ? null : normalCdf(sdsNow) * 100,
    sdsTrail,
  };
}

function computeSdsTrail(
  table: readonly LMSRow[] | null,
  birthSec: number,
  sorted: readonly AdviceMeasurement[],
): SdsTrailEntry[] {
  if (!table) {
    return sorted.map((m) => ({ ageYr: date2age(birthSec, m.occurredAt), sds: null }));
  }
  return sorted.map((m) => {
    const ageYr = date2age(birthSec, m.occurredAt);
    const lms = interpolateLMS(table, ageYr);
    return { ageYr, sds: lms ? valueToSDS(m.value, lms) : null };
  });
}

// ─── Weight (calculate_advice_wgt 0-1y) ──────────────────────────────

export type WeightAdviceMsgCode =
  | 2012 // need a prior measurement
  | 2015 // missing measurement date
  | 2016 // missing dob
  | 2018 // missing weight
  | 2019 // invalid sex
  | 2021 // ≥19 yr (out of paediatric range)
  | 2024 // SDS undefined (age outside table)
  | 2026 // ≥1 yr (wfh branch not implemented)
  | 2031 // no referral
  | 2074 // ondergewicht (SDS < -2)
  | 2075; // sterke gewichtsafname (Δ < -1 SD)

const WEIGHT_MESSAGES: Record<WeightAdviceMsgCode, string> = {
  2012: 'Need a second weight reading to evaluate the trend.',
  2015: 'Need a measurement date to evaluate weight.',
  2016: 'Need the date of birth to evaluate weight.',
  2018: 'Need the latest weight to evaluate.',
  2019: 'Set the baby’s sex to evaluate weight.',
  2021: 'These growth alerts apply up to age 18.',
  2024: 'Cannot compute SDS for the latest measurement (age outside the table).',
  2026: 'Weight-for-height advice (1–2 yr) is not implemented yet.',
  2031: 'Weight trend looks normal — no referral indicated.',
  2074: 'Underweight (SDS < −2) — review feeding and check for symptoms.',
  2075: 'Strong weight drop (> 1 SD vs an earlier reading) — review feeding and check for symptoms.',
};

export type CalculateWeightAdviceInput = {
  sex: Sex | null | undefined;
  birthSec: number | null | undefined;
  measurements: readonly { occurredAt: number; weightKg: number }[];
  testGain?: boolean;
  /** Gestational age at birth in completed days. <259 routes to the NL2012
   *  preterm weight reference (Bocca-Tjeertes 2012) for the closest tabulated
   *  ga week (25..36, clamped). Null/undefined → assume term. */
  gaDays?: number | null;
  /** Reference standard for the SDS computation. Defaults to NL5 (what the
   *  JGZ uses). Preterm gaDays override this — they always use NL2012. */
  reference?: 'nl3' | 'nl4' | 'nl5' | 'who';
};

export function calculateWeightAdvice(
  input: CalculateWeightAdviceInput,
): AdviceResult<WeightAdviceMsgCode> {
  const { sex, birthSec, measurements, testGain = true, gaDays, reference = 'nl5' } = input;
  const wrap = (c: WeightAdviceMsgCode, s: number | null, t: SdsTrailEntry[]) =>
    buildResult(c, WEIGHT_MESSAGES[c], s, t);

  if (sex !== 'M' && sex !== 'F') return wrap(2019, null, []);
  if (!measurements.length) return wrap(2015, null, []);
  if (birthSec == null) return wrap(2016, null, []);

  const sorted: AdviceMeasurement[] = [...measurements]
    .map((m) => ({ occurredAt: m.occurredAt, value: m.weightKg }))
    .sort((a, b) => a.occurredAt - b.occurredAt);
  const now = sorted[sorted.length - 1];

  if (now.value == null || !Number.isFinite(now.value)) return wrap(2018, null, []);

  const ageNow = date2age(birthSec, now.occurredAt);
  if (ageNow >= 19) return wrap(2021, null, []);
  // Term babies → selected reference wfa (default NL5); preterm
  // (ga_days<259) → NL2012 preterm wfa for the matching ga week. Mirrors
  // R's pick_reference_wgt.
  const weightTable = pickWeightReference(reference, sex, gaDays ?? null);
  if (ageNow >= 1.0) return wrap(2026, null, computeSdsTrail(weightTable, birthSec, sorted));

  const trail = computeSdsTrail(weightTable, birthSec, sorted);
  const sdsNow = trail[trail.length - 1]?.sds ?? null;
  if (sdsNow == null) return wrap(2024, null, trail);

  if (sdsNow < -2.0) return wrap(2074, sdsNow, trail);

  if (testGain) {
    const priors = trail.slice(0, -1);
    if (!priors.length) return wrap(2012, sdsNow, trail);
    // R uses strict `< -1.0` (calculate_advice_wgt.R line 109). We skip the
    // 2013 height-missing guard because the 0-1y branch is age-based (R
    // lines 86-92) and our app doesn't pair height with each weight entry.
    for (const p of priors) {
      if (p.sds == null) continue;
      if (sdsNow - p.sds < -1.0) return wrap(2075, sdsNow, trail);
    }
  }

  return wrap(2031, sdsNow, trail);
}

// ─── Head circumference (calculate_advice_hdc 0-1y) ──────────────────

export type HdcAdviceMsgCode =
  | 3015 | 3016 | 3018 | 3019 | 3021 | 3022 | 3023 | 3024 | 3025
  | 3031 // no referral
  | 3041 // macrocephaly suspicion (z > 2.5)
  | 3042 // rapid increase (>2.5 SD vs prior)
  | 3043 // microcephaly suspicion (z < -2)
  | 3044 // >0.5 SD increase within 6 months
  | 3045; // >0.5 SD decrease

const HDC_MESSAGES: Record<HdcAdviceMsgCode, string> = {
  3015: 'Need a measurement date to evaluate head circumference.',
  3016: 'Need the date of birth to evaluate head circumference.',
  3018: 'Need the latest head circumference to evaluate.',
  3019: 'Set the baby’s sex to evaluate head circumference.',
  3021: 'Head-circumference screening is intended for ages under 1 year.',
  3022: 'Need at least two measurements with dates to evaluate the trend.',
  3023: 'Need at least two valid measurements to evaluate the trend.',
  3024: 'Cannot compute SDS for the latest head measurement (age outside the table).',
  3025: 'No prior SDS available to compare against.',
  3031: 'Head circumference looks normal — no referral indicated.',
  3041: 'Head circumference above +2.5 SD — discuss with paediatrician.',
  3042: 'Rapid head growth (> 2.5 SD vs an earlier reading) — discuss with paediatrician.',
  3043: 'Head circumference below −2 SD — discuss with paediatrician.',
  3044: 'Head growth jumped > 0.5 SD within 6 months — discuss with paediatrician.',
  3045: 'Head circumference dropped > 0.5 SD vs an earlier reading — discuss with paediatrician.',
};

export type CalculateHdcAdviceInput = {
  sex: Sex | null | undefined;
  birthSec: number | null | undefined;
  /** Head circumference in cm. */
  measurements: readonly { occurredAt: number; hcCm: number }[];
  testGain?: boolean;
  /** Reference standard. All NL references fall back to NL1997 for HC (NL5
   *  didn't publish a new HC reference), so practically this only matters
   *  when 'who' is selected. */
  reference?: 'nl3' | 'nl4' | 'nl5' | 'who';
};

export function calculateHdcAdvice(
  input: CalculateHdcAdviceInput,
): AdviceResult<HdcAdviceMsgCode> {
  const { sex, birthSec, measurements, testGain = true, reference = 'nl5' } = input;
  const wrap = (c: HdcAdviceMsgCode, s: number | null, t: SdsTrailEntry[]) =>
    buildResult(c, HDC_MESSAGES[c], s, t);

  if (sex !== 'M' && sex !== 'F') return wrap(3019, null, []);
  if (!measurements.length) return wrap(3015, null, []);
  if (birthSec == null) return wrap(3016, null, []);

  const sorted: AdviceMeasurement[] = [...measurements]
    .map((m) => ({ occurredAt: m.occurredAt, value: m.hcCm }))
    .sort((a, b) => a.occurredAt - b.occurredAt);
  const now = sorted[sorted.length - 1];

  if (now.value == null || !Number.isFinite(now.value)) return wrap(3018, null, []);

  const ageNow = date2age(birthSec, now.occurredAt);
  if (ageNow >= 1.0) return wrap(3021, null, []);

  // Dutch JGZ HC reference. NL3/NL5 fall back to NL1997 via the matrix;
  // WHO stays on WHO if available.
  const trail = computeSdsTrail(
    getReferenceTable(reference, 'head_circumference', sex),
    birthSec,
    sorted,
  );
  const sdsNow = trail[trail.length - 1]?.sds ?? null;
  if (sdsNow == null) return wrap(3024, null, trail);

  if (sdsNow > 2.5) return wrap(3041, sdsNow, trail);
  if (sdsNow < -2.0) return wrap(3043, sdsNow, trail);

  if (testGain) {
    const priors = trail.slice(0, -1);
    if (!priors.length) return wrap(3022, sdsNow, trail);
    const priorsWithSds = priors.filter((p) => p.sds != null) as { ageYr: number; sds: number }[];
    if (!priorsWithSds.length) return wrap(3025, sdsNow, trail);

    // Rapid increase: ANY prior with delta > 2.5 SD.
    for (const p of priorsWithSds) {
      if (sdsNow - p.sds > 2.5) return wrap(3042, sdsNow, trail);
    }
    // 6-month window: increase > 0.5 SD over the last half year.
    const ageNowYr = trail[trail.length - 1].ageYr;
    for (const p of priorsWithSds) {
      if (ageNowYr - p.ageYr <= 0.5 && sdsNow - p.sds > 0.5) return wrap(3044, sdsNow, trail);
    }
    // Decrease: ANY prior with delta < -0.5 SD.
    for (const p of priorsWithSds) {
      if (sdsNow - p.sds < -0.5) return wrap(3045, sdsNow, trail);
    }
  }

  return wrap(3031, sdsNow, trail);
}

// ─── Height (calculate_advice_hgt 0-3y) ──────────────────────────────
// Simplified port: implements the cross-sectional SDS branches. The 1-3y
// section in R has extra arms that depend on birth-weight z, birth-length
// z, target-height z and gestational age — we surface those gradually as
// the inputs become available (T78 birth-z, T76 ga_days, existing TH).

export type HgtAdviceMsgCode =
  | 1011 | 1015 | 1016 | 1018 | 1019 | 1021 | 1022 | 1024
  | 1031 // no referral
  | 1044 // z < -2.5 (short stature)
  | 1045 // z < -3 (severe short stature)
  | 1048 // z > 3 (very tall)
  | 1077; // z > 1 at <3y (warrants attention)

const HGT_MESSAGES: Record<HgtAdviceMsgCode, string> = {
  1011: 'Need a second length reading to evaluate the trend.',
  1015: 'Need a measurement date to evaluate length.',
  1016: 'Need the date of birth to evaluate length.',
  1018: 'Need the latest length to evaluate.',
  1019: 'Set the baby’s sex to evaluate length.',
  1021: 'Length screening applies up to age 18.',
  1022: 'Length screening starts after the first month.',
  1024: 'Cannot compute SDS for the latest length (age outside the table).',
  1031: 'Length trend looks normal — no referral indicated.',
  1044: 'Length below −2.5 SD — discuss with paediatrician.',
  1045: 'Length below −3 SD — discuss with paediatrician.',
  1048: 'Length above +3 SD — discuss with paediatrician.',
  1077: 'Length above +1 SD at this age — keep monitoring.',
};

export type CalculateHgtAdviceInput = {
  sex: Sex | null | undefined;
  birthSec: number | null | undefined;
  /** Length in cm. */
  measurements: readonly { occurredAt: number; lengthCm: number }[];
  /** Currently selected reference; only NL5 is wired through advice. */
  reference?: 'nl3' | 'nl4' | 'nl5' | 'who';
};

export function calculateHgtAdvice(
  input: CalculateHgtAdviceInput,
): AdviceResult<HgtAdviceMsgCode> {
  const { sex, birthSec, measurements, reference = 'nl5' } = input;
  const wrap = (c: HgtAdviceMsgCode, s: number | null, t: SdsTrailEntry[]) =>
    buildResult(c, HGT_MESSAGES[c], s, t);

  if (sex !== 'M' && sex !== 'F') return wrap(1019, null, []);
  if (!measurements.length) return wrap(1015, null, []);
  if (birthSec == null) return wrap(1016, null, []);

  const sorted: AdviceMeasurement[] = [...measurements]
    .map((m) => ({ occurredAt: m.occurredAt, value: m.lengthCm }))
    .sort((a, b) => a.occurredAt - b.occurredAt);
  const now = sorted[sorted.length - 1];

  if (now.value == null || !Number.isFinite(now.value)) return wrap(1018, null, []);

  const ageNow = date2age(birthSec, now.occurredAt);
  if (ageNow >= 18) return wrap(1021, null, []);
  if (ageNow < 0.0833) return wrap(1022, null, []);

  const trail = computeSdsTrail(
    getReferenceTable(reference, 'length', sex),
    birthSec,
    sorted,
  );
  const sdsNow = trail[trail.length - 1]?.sds ?? null;
  if (sdsNow == null) return wrap(1024, null, trail);

  // 0-3y branch from R lines 104-115.
  if (ageNow < 3.0) {
    if (sdsNow < -3.0) return wrap(1045, sdsNow, trail);
    if (sdsNow < -2.5) return wrap(1044, sdsNow, trail);
    if (sdsNow > 3.0) return wrap(1048, sdsNow, trail);
    // R line 114: "z1 > 2.5 && all(is.na(z0)) && test_gain → 1011" (need a
    // prior to evaluate further). Anything > 2.5 with a valid prior falls
    // through to 1077 below. The standalone 1047 I had here was a
    // transcription artifact — 1047 lives in the 3-10y branch, not 0-3y.
    if (sdsNow > 2.5 && trail.slice(0, -1).every((p) => p.sds == null)) {
      return wrap(1011, sdsNow, trail);
    }
    if (sdsNow > 1.0) return wrap(1077, sdsNow, trail);
    return wrap(1031, sdsNow, trail);
  }

  // ≥3y not implemented here — fall back to a neutral OK so we don't
  // mis-fire on babies who happen to age past 3 before we ship that branch.
  return wrap(1031, sdsNow, trail);
}
